import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';

import { PayloadItemView } from '../../models/meta-params/payload-item-view';

export interface PayloadLedgerConfiguration {
    title: string;
    emptyDescription: string;
    releaseRoutePrefix: string;
    orchestrationRoutePrefix: string;
    allowMutations: boolean;
    allowExtraction: boolean;
}

/** Shared, API-free payload rendering used by shipment and delivery workspaces. */
@Component({
    selector: 'app-payload-ledger',
    standalone: true,
    templateUrl: './payload-ledger.component.html',
    styleUrls: ['./payload-ledger.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, RouterModule, ButtonModule, TagModule]
})
export class PayloadLedgerComponent {
    readonly items = input<readonly PayloadItemView[]>([]);
    readonly configuration = input.required<PayloadLedgerConfiguration>();
    readonly extracting = input(false);
    readonly saving = input(false);
    readonly releasePartyLabels = input<Record<number, string>>({});

    readonly extractDocument = output<File>();
    readonly add = output<void>();
    readonly edit = output<{ item: PayloadItemView; index: number }>();
    readonly remove = output<{ item: PayloadItemView; index: number }>();

    onDocumentSelected(event: Event): void {
        const file = (event.target as HTMLInputElement | null)?.files?.item(0);
        if (file) this.extractDocument.emit(file);
        // Allows selecting the same file after correcting its contents.
        (event.target as HTMLInputElement | null)?.value && ((event.target as HTMLInputElement).value = '');
    }

    description(item?: PayloadItemView): string {
        return item?.warehouseItemName || item?.itemDescription || item?.itemDesciption || item?.itemType?.name || 'Payload';
    }

    quantity(item?: PayloadItemView): string {
        if (item?.tenantUomId && item.enteredQuantity != null) {
            return `${item.enteredQuantity} ${item.tenantUomName || 'Tenant UOM'} (${item.itemQuantity?.quantity ?? '-'} ${item.itemQuantity?.uom?.name || 'Each'})`;
        }
        const value = item?.itemQuantity?.quantity;
        const unit = item?.itemQuantity?.uom?.name || '';
        return value ? `${value} ${unit}`.trim() : '-';
    }

    totalWeight(item?: PayloadItemView): string {
        const total = Number(item?.itemWeight ?? 0) * Number(item?.itemQuantity?.quantity ?? 0);
        const unit = item?.weightUnit?.shortName || '';
        return total > 0 ? `${Number(total.toFixed(3))} ${unit}`.trim() : 'Weight pending';
    }

    itemSize(item?: PayloadItemView): string { return item?.itemSize || '-'; }
    source(item?: PayloadItemView): string { return [item?.sourceWarehouseLabel, item?.sourceZoneLabel, item?.conditionLabel].filter(Boolean).join(' / ') || '-'; }
    movement(item?: PayloadItemView): string { return [item?.linkedWarehouseTransferNumber, item?.linkedWarehouseDispatchNumber].filter(Boolean).join(' / ') || '-'; }
}
